// @react
import React from 'react'
// @types
import type { ReactElement } from 'react'
import type {
  GetUserQuery,
  GetUserQueryVariables,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import DocumentTitle from 'react-document-title'
import { makeStyles } from '@material-ui/core/styles'
import { useMediaQuery } from '@material-ui/core'
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import AdditionalMemberResources from 'modules/benefitsCenter/components/AdditionalMemberResources'
import MemberResources from 'modules/benefitsCenter/components/MemberResources'
import MyATDPageWrapper from 'modules/common/components/MyATDPageWrapper'
import Spacer from '@bit/atd.web.spacer'
import theme from '@bit/atd.web.theme'
// @module components
import MemberBenefits from 'modules/benefitsCenter/components/MemberBenefits'
import Community from 'modules/benefitsCenter/components/Community'
import MembershipDetails from 'modules/benefitsCenter/components/MembershipDetails'
import FeaturedBenefit from 'modules/benefitsCenter/components/FeaturedBenefit'
// @module common components
import Reminder from 'modules/common/components/Reminder'
import InfoBanner from 'modules/common/components/InfoBanner'
// @common
import config from 'constants/config'

const useStyles = makeStyles({
  container: {
    marginTop: '-24px',
  },
  icon: {
    '& svg': {
      width: '28px !important',
      height: '28px !important',
      margin: '10px 0px 0px 10px',
    },
  },
})

/**
 * 4.0 MyATD - Benefits Center
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=866%3A3045
 */

const title = 'Benefits Center'

const BenefitsCenter = (): ReactElement => {
  /******************************************
   * HOOKS
   ******************************************/

  const { loading, error, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-only' })

  const isMobile = useMediaQuery(
    theme.breakpoints.down('sm'),
    config.mediaQuery,
  )

  const classes = useStyles()

  /******************************************
   * RENDER
   ******************************************/

  const desktopLayout = (
    <Grid container spacing={4}>
      <Grid item xs={3}>
        <Grid container>
          <Grid item xs={12}>
            <MembershipDetails />
          </Grid>
          <Grid item xs={12}>
            <Spacer size={24} />
            <MemberResources />
          </Grid>
          <Grid item xs={12}>
            <Spacer size={24} />
            <AdditionalMemberResources />
          </Grid>
          <Grid item xs={12}>
            <Spacer size={24} />
            <Community />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={9}>
        <Grid container>
          <Grid item xs={12}>
            <MemberBenefits />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )

  const mobileLayout = (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <MembershipDetails />
      </Grid>
      <Grid item xs={12}>
        <MemberBenefits />
      </Grid>
      <Grid item xs={12}>
        <MemberResources />
      </Grid>
      <Grid item xs={12}>
        <AdditionalMemberResources />
      </Grid>
      <Grid item xs={12}>
        <Community />
      </Grid>
    </Grid>
  )

  const claimResource = data?.user?.membership?.claimResource

  return (
    <MyATDPageWrapper>
      <DocumentTitle title={`${title} | ATD`}>
        <>
          <div className={classes.container}>
            {/* Reminder / Info Banner */}
            <Container>
              {/* Reminder */}
              <Reminder
                isHiddenComponent={
                  claimResource?.showBanner ? <Spacer size={24} /> : <div />
                }
              />

              {/* Info Banner */}
              {!loading && !error && claimResource?.showBanner ? (
                <div className={classes.icon}>
                  <InfoBanner
                    title="You have a resource center available!"
                    icon="union"
                    btnText="Choose your resource center"
                    btnLink={claimResource?.claimLink}
                    isVisible
                  />
                  <Spacer size={24} />
                </div>
              ) : (
                <span />
              )}
            </Container>

            {/* Featured Benefit */}
            <FeaturedBenefit />
          </div>

          {/* Main */}
          <Container>
            <Spacer size={isMobile ? 24 : 40} />

            {/* Content */}
            {isMobile ? mobileLayout : desktopLayout}
          </Container>
        </>
      </DocumentTitle>
    </MyATDPageWrapper>
  )
}

export default BenefitsCenter
